import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DataGrid,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { Button } from 'react-bootstrap';
import ExportExcelButton from '../../../components/ExportExcelButton';
import { ReleaseResponse, ReleaseTableProps } from '../types';
import { useNavigate } from 'react-router-dom';
import { getUiInterface } from '../../../locales/translationUtils';

const ReleaseTable: React.FC<ReleaseTableProps> = ({
  apiResponse,
  rows,
  columns,
  paginationModel,
  setPaginationModel,
  sortModel,
  onSortModelChange,
  onExportExcel,
  exportandoExcel,
 }) => {
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [firstSelectedSupplier, setFirstSelectedSupplier] = useState<
    string | undefined
  >(undefined);

  // Com paginacao server-side, `rows` so tem a pagina atual - mas a selecao
  // (rowSelectionModel, com keepNonExistentRowsSelected) pode incluir ids de
  // paginas ja visitadas. Acumula toda linha que passar pela grid num Map por
  // id, pra sempre montar o objeto completo de uma linha selecionada, esteja
  // ela na pagina atual ou nao.
  const knownRowsRef = useRef<Map<number, ReleaseResponse>>(new Map());
  useEffect(() => {
    rows.forEach((row) => knownRowsRef.current.set(row.id, row));
  }, [rows]);

  const selectedRows = useMemo(
    () =>
      rowSelectionModel
        .map((id) => knownRowsRef.current.get(Number(id)))
        .filter((row): row is ReleaseResponse => row !== undefined),
    [rowSelectionModel]
  );

  const onToRelease = () => {
    navigate('/release/to-release', { state: { selectedRows } });
  };

  const onToClean = () => {
    setFirstSelectedSupplier(undefined);
    setRowSelectionModel([]);
  };

  function handleSelectionChange(ids: GridRowSelectionModel) {
    if (ids.length === 0) {
      setFirstSelectedSupplier(undefined);
    } else if (rowSelectionModel.length === 0) {
      // Primeira selecao desta rodada (nada estava selecionado antes): trava
      // o fornecedor da primeira linha escolhida para isRowSelectable.
      const firstSelectedRow = knownRowsRef.current.get(Number(ids[0]));
      setFirstSelectedSupplier(firstSelectedRow?.supplierName);
    }

    setRowSelectionModel(ids);
  }

  function isRowSelectable(params: GridRowParams) {
    return (
      firstSelectedSupplier === undefined ||
      params.row.supplierName === firstSelectedSupplier
    );
  }

  return (
    // Preenche a altura que o pai (flex) reservar para a tabela - o pai e quem
    // decide ate onde a grid vai, este componente so ocupa 100% dela.
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        rowHeight={36}
        columnHeaderHeight={40}
        pageSizeOptions={[10, 20, 40, 100]}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={apiResponse?.data?.totalItems ?? 0}
        sortingMode='server'
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        // E1: sem isso, o MUI descarta da selecao qualquer linha que saia da
        // pagina atual - trocar de pagina "esquecia" o que already tinha sido
        // marcado antes.
        keepNonExistentRowsSelected
        getRowClassName={(params) => {
          const fromValue = params.row.fromIdentification;
          const rowIndex = params.indexRelativeToCurrentPage; 

          if (fromValue === 'LAX') {
            return rowIndex % 2 === 0 ? 'highlight-lax-even' : 'highlight-lax-odd';
          }
          if (fromValue === 'CVG') {
            return rowIndex % 2 === 0 ? 'highlight-cvg-even' : 'highlight-cvg-odd';
          }
          if (fromValue === 'DFW') {
            return rowIndex % 2 === 0 ? 'highlight-dfw-even' : 'highlight-dfw-odd';
          }
          if (fromValue === 'MIA') {
            return rowIndex % 2 === 0 ? 'highlight-mia-even' : 'highlight-mia-odd';
          }

          return '';
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'white',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#b2d1ff',
          },
          '& .highlight-lax-even': {
            backgroundColor: '#2600A0 !important',
            color: '#FFFFFF !important',
          },
          '& .highlight-lax-odd': {
            backgroundColor: '#0004B9 !important',
            color: '#FFFFFF !important',
          },

          '& .highlight-cvg-even': {
            backgroundColor: '#89E17E !important',
          },
          '& .highlight-cvg-odd': {
            backgroundColor: '#B1EEB6 !important',
          },
          '& .highlight-dfw-even': {
            backgroundColor: '#fff1c1 !important',
          },
          '& .highlight-dfw-odd': {
            backgroundColor: '#fff7a8 !important',
          },
          '& .highlight-mia-even': {
            backgroundColor: '#C6C3BE !important',
          },
          '& .highlight-mia-odd': {
            backgroundColor: '#FFFCF8 !important',
          },
          '& .MuiTablePagination-selectLabel': {
            marginTop: '14px',
          },
          '& .MuiTablePagination-displayedRows': {
            marginTop: '14px',
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
            display: 'none',
          },
          "& .MuiDataGrid-cell[id='id']": {
            cursor: 'pointer !important',
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within": {
            outline: "none",
          }
        }}
        isRowSelectable={isRowSelectable}
      />
      </div>

      <div style={{ marginTop: 16, paddingBottom: 16, textAlign: 'right', flexShrink: 0 }}>
        <ExportExcelButton onClick={onExportExcel} exportando={exportandoExcel} />

        <span style={{ margin: '0 8px' }} />

        <Button
          variant="primary"
          onClick={onToClean}
          disabled={selectedRows.length === 0}
        >
          {getUiInterface('clean')}
        </Button>

        <span style={{ margin: '0 8px' }} />

        <Button
          variant="primary"
          onClick={onToRelease}
          disabled={selectedRows.length === 0}
        >
          {getUiInterface('toRelease')}
        </Button>
      </div>
    </div>
  );
};

export default ReleaseTable;
